import { emberClient } from '@ember/api-client'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  type PortalNavItem,
  PortalSidebar,
  usePortalSidebar,
} from '@ember/ui'
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Building2,
  CreditCard,
  Gift,
  Layers,
  LayoutDashboard,
  PanelLeft,
  Settings,
  ShieldCheck,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { clearTokens, getContextToken, getTokenPayload } from '../../lib/auth'
import { ChangePasswordDialog } from '../ChangePasswordDialog'

const navGroups: PortalNavItem[][] = [
  [{ title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }],
  [
    { title: 'Tenants', href: '/tenants', icon: Building2 },
    { title: 'Plans', href: '/plans', icon: Layers },
  ],
  [
    {
      title: 'Finance',
      href: '/finance',
      icon: CreditCard,
      subItems: [
        { title: 'Invoices', href: '/invoices' },
        { title: 'Commissions', href: '/commissions' },
        { title: 'Agreements', href: '/agreements' },
      ],
    },
    { title: 'Referral', href: '/referral', icon: Gift },
  ],
  [
    {
      title: 'Access',
      href: '/access',
      icon: ShieldCheck,
      subItems: [
        { title: 'Team', href: '/team' },
        { title: 'Roles', href: '/roles' },
      ],
    },
    { title: 'Settings', href: '/settings', icon: Settings },
  ],
]

export function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const sidebar = usePortalSidebar('partner_sidebar_collapsed', location.pathname)
  const payload = useMemo(() => getTokenPayload(), [])
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  const handleLogout = async () => {
    try {
      const token = getContextToken()
      if (token) {
        await emberClient.withContextToken(token).api.auth.logout.$post()
      }
    } catch (err) {
      console.error('Logout API call failed:', err)
    } finally {
      clearTokens()
      navigate({
        to: '/login',
        search: { message: undefined },
      })
    }
  }

  const partnerName = payload?.contextName || 'Partner'
  const userName = payload?.userName || payload?.email || 'User'
  const initials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="flex min-h-screen w-full bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50">
      <PortalSidebar
        groups={navGroups}
        pathname={location.pathname}
        accent="teal"
        badge="Partner Portal"
        collapsed={sidebar.collapsed}
        mobileOpen={sidebar.mobileOpen}
        onMobileOpenChange={sidebar.setMobileOpen}
        renderLink={({ href, children, ...rest }) => (
          <Link to={href} {...rest}>
            {children}
          </Link>
        )}
      />

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={sidebar.toggle}
              aria-label="Toggle sidebar"
              className="size-9 flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors focus:outline-none"
            >
              <PanelLeft className="size-5" />
            </button>
            <div className="text-sm font-semibold text-zinc-500 truncate">{partnerName}</div>
          </div>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 rounded-md p-1"
                >
                  <div className="size-8 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 flex items-center justify-center text-xs font-bold border border-teal-200 dark:border-teal-800">
                    {initials}
                  </div>
                  <span className="hidden sm:inline">{userName}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsChangePasswordOpen(true)}>
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-rose-600 dark:text-rose-400 focus:text-rose-600 dark:focus:text-rose-400 cursor-pointer"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <ChangePasswordDialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen} />
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
